import React, { createContext, useContext, useState } from 'react';
import { AlertColor, Snackbar } from '@mui/material';
import { StyledAlert } from 'components/StyledAlert';
import { CheckCircle, Error as ErrorIcon, Info } from '@mui/icons-material';

type ToastProps = {
    open: boolean;
    message: string;
    type?: AlertColor; // 'success' | 'error' | 'warning' | 'info'
    duration?: number;
};

type ToastContextType = {
    toast: (message: string, type?: AlertColor, duration?: number) => void;
};

const getIcon = (severity?: AlertColor) => {
    switch (severity) {
        case 'success':
            return <CheckCircle fontSize="inherit" />;
        case 'error':
            return <ErrorIcon fontSize="inherit" />;
        case 'warning':
            return <ErrorIcon fontSize="inherit" />;
        default:
            return <Info fontSize="inherit" />;
    }
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toastState, setToastState] = useState<ToastProps>({
        open: false,
        message: '',
        type: 'info',
        duration: 3000,
    });

    const toast = (message: string, type: AlertColor = 'info', duration?: number) => {
        setToastState({ open: true, message, type, duration: duration ?? 3000 });
    };

    const handleClose = () => {
        setToastState((prev) => ({ ...prev, open: false }));
    };

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            <Snackbar
                open={toastState.open}
                autoHideDuration={toastState.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <StyledAlert onClose={handleClose} severity={toastState.type} icon={getIcon(toastState.type)}>
                    {toastState.message}
                </StyledAlert>
            </Snackbar>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) throw new Error('useToast must be used within ToastProvider');
    return context.toast;
}
